import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fromAuth, useAppDispatch } from '../store';

interface GetTokenResponse {
  payload: {
    accessToken: string;
    readOnly?: boolean;
  };
  requestId: string;
  type: 'katalon:getConfig:response';
  version: number;
}

interface PortalAuthState {
  accessToken: string | null;
  readOnly: boolean;
  isReady: boolean;
}

export const usePortalAuthenticate = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useSelector(fromAuth.selectIsAuthenticated);
  const user = useSelector(fromAuth.selectUser);

  const [portalAuth, setPortalAuth] = useState<PortalAuthState>({
    accessToken: null,
    readOnly: false,
    isReady: false,
  });

  const request = {
    requestId: `getConfig-${Date.now()}`,
    type: 'katalon:getConfig',
    version: 1,
  };

  useEffect(() => {
    // Send initial request to portal for configuration
    const targetOrigin = '*';
    window.parent.postMessage(request, targetOrigin);

    const handlePortalMessage = async (event: MessageEvent) => {
      // Validate origin
      const allowedOrigins = [
        process.env.REACT_APP_INTERNAL_PORTAL_URL,
      ].filter(Boolean);

      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      const message = event.data as GetTokenResponse;
      if (message.payload && message.payload.accessToken) {
        try {
          await dispatch(fromAuth.doResumeWithOnlyAccessToken({
            jwt: message.payload.accessToken,
          }));
          setPortalAuth({
            accessToken: message.payload.accessToken,
            readOnly: message.payload.readOnly || false,
            isReady: true,
          });
        } catch (error) {
          setPortalAuth(prev => ({ ...prev, isReady: true }));
        }
      }
    };

    // Listen for messages from the portal
    window.addEventListener('message', handlePortalMessage);

    return () => window.removeEventListener('message', handlePortalMessage);
  }, [dispatch]);

  return {
    isAuthenticated,
    user,
    readOnly: portalAuth.readOnly,
    accessToken: portalAuth.accessToken,
    isReady: portalAuth.isReady,
  };
};
